import _ from 'lodash'
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useId,
  useMemo,
} from 'react'
import { DrilldownFocusItemsType, RolodexHistogramFilter } from 'types'

export interface DrilldownFilterContextType {
  precedingFilters: readonly RolodexHistogramFilter[]
  filters: ReadonlyMap<string, readonly RolodexHistogramFilter[]>
  setFilters: Dispatch<
    SetStateAction<ReadonlyMap<string, readonly RolodexHistogramFilter[]>>
  >
  inheritedFocusItems: DrilldownFocusItemsType | undefined
  focusItems: DrilldownFocusItemsType | undefined
  setFocusItems: Dispatch<SetStateAction<DrilldownFocusItemsType | undefined>>
}

const EMPTY_DRILLDOWN_FILTER_CONTEXT = {
  precedingFilters: [],
  filters: new Map(),
  setFilters: () => {},
  inheritedFocusItems: undefined,
  focusItems: undefined,
  setFocusItems: () => {},
}

export const DrilldownFilterContext = createContext<DrilldownFilterContextType>(
  EMPTY_DRILLDOWN_FILTER_CONTEXT
)

export function useFilters(
  filters: readonly RolodexHistogramFilter[] | undefined,
  id?: string
): void {
  const { setFilters: _setFilterMap } = useContext(DrilldownFilterContext)
  const defaultId = useId()
  const filterId = id ?? defaultId

  const setFilterMap = useMemo(
    () => _.debounce(_setFilterMap, 200),
    [_setFilterMap]
  )

  useEffect(() => {
    setFilterMap((prev) => {
      if (_.isEqual(prev.get(filterId), filters)) {
        return prev
      }
      const newMap = new Map(prev).set(filterId, filters ?? [])
      return newMap
    })
    return () => {
      setFilterMap((prev) => {
        const newMap = new Map(prev)
        newMap.delete(filterId)
        return newMap
      })
    }
  }, [filters, filterId, setFilterMap])
}

export function useGetPrecedingFilters(): readonly RolodexHistogramFilter[] {
  const { precedingFilters } = useContext(DrilldownFilterContext)
  return useMemo(() => precedingFilters, [precedingFilters])
}

export function useFilterToSites(siteIds: ReadonlySet<string>): void {
  useFilterToIds(siteIds, 'site')
}

export function useFilterToIds(
  ids: ReadonlySet<string> | null,
  type: string
): void {
  useFilterToValues(ids, type, 'id', 'field')
}

export function useFilterToValues(
  values: ReadonlySet<string> | null,
  type: string,
  filterPropertyName: string,
  filterPropertyNameKey: 'field' | 'key' = 'field'
): void {
  const filters = useMemo<readonly RolodexHistogramFilter[]>(
    () =>
      values
        ? [
            {
              type: type,
              [filterPropertyNameKey]: filterPropertyName,
              op: 'in',
              value: [...values],
            },
          ]
        : [],
    [filterPropertyName, filterPropertyNameKey, type, values]
  )
  useFilters(filters)
}

export function useFocusItems(): [
  DrilldownFocusItemsType | undefined,
  Dispatch<SetStateAction<DrilldownFocusItemsType | undefined>>,
] {
  const { inheritedFocusItems, focusItems, setFocusItems } = useContext(
    DrilldownFilterContext
  )
  return useMemo(
    () => [
      focusItems === undefined ? inheritedFocusItems : focusItems,
      setFocusItems,
    ],
    [focusItems, inheritedFocusItems, setFocusItems]
  )
}
